"use client";

import Link from "next/link";
import { useState, useEffect } from "react";

// ─── Section registry ─────────────────────────────────────────────────────────

const SECTIONS = [
  { id: "overview", label: "Overview" },
  {
    id: "how-it-works", label: "How It Works", children: [
      { id: "pipeline-stages", label: "Pipeline Stages" },
      { id: "deduplication", label: "Deduplication Engine" },
    ],
  },
  {
    id: "agent-pipeline", label: "Agent Pipeline", children: [
      { id: "agent-chain", label: "4-Agent Chain" },
      { id: "agent-outputs", label: "Outputs" },
    ],
  },
  {
    id: "architecture", label: "Architecture", children: [
      { id: "system-overview", label: "System Overview" },
      { id: "job-state-machine", label: "Job State Machine" },
    ],
  },
  { id: "tech-stack", label: "Tech Stack" },
  {
    id: "configuration", label: "Configuration", children: [
      { id: "env-vars", label: "Environment Variables" },
      { id: "agent-config", label: "Agent Models" },
    ],
  },
  { id: "quick-start", label: "Quick Start" },
  {
    id: "deployment", label: "Deployment", children: [
      { id: "scripts", label: "Scripts Reference" },
      { id: "production-checklist", label: "Production Checklist" },
    ],
  },
  {
    id: "integrations", label: "Integrations", children: [
      { id: "clickup", label: "ClickUp" },
      { id: "github-plans", label: "GitHub Plans" },
    ],
  },
  { id: "future", label: "Future Enhancements" },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DocsPage() {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: "-20% 0% -70% 0%", threshold: 0 }
    );
    document.querySelectorAll("section[id]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#0d1117] text-slate-200">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0d1117]/95 backdrop-blur-md border-b border-white/[0.06] h-14 flex items-center px-6">
        <div className="flex items-center gap-3 flex-1">
          <Link href="/" className="flex items-center gap-2 group">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/ghl-logo.jpeg" alt="GoHighLevel" width={24} height={24}
              className="w-6 h-6 rounded-md object-contain bg-[#021b49] p-0.5" />
            <span className="text-sm font-semibold text-slate-300 group-hover:text-white transition-colors">Roadmap Radar</span>
          </Link>
          <span className="text-slate-700 text-sm">/</span>
          <span className="text-sm font-medium text-slate-500">Documentation</span>
        </div>
        <div className="flex items-center gap-3">
          <Link href="/" className="text-xs text-slate-500 hover:text-slate-200 transition-colors font-medium">← Back to home</Link>
          <Link href="/login" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-lg transition-colors">
            Sign in
          </Link>
        </div>
      </header>

      <div className="flex pt-14">
        {/* Left sidebar */}
        <nav className="fixed top-14 left-0 bottom-0 w-60 overflow-y-auto border-r border-white/[0.06] bg-[#0d1117] py-6 px-4 hidden lg:block">
          <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-3 px-2">On this page</div>
          {SECTIONS.map((section) => (
            <div key={section.id}>
              <a
                href={`#${section.id}`}
                onClick={() => setActiveSection(section.id)}
                className={`block px-2 py-1.5 rounded-md text-sm font-medium transition-colors mb-0.5 ${
                  activeSection === section.id
                    ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                    : "text-slate-500 hover:text-slate-200 hover:bg-white/5"
                }`}
              >
                {section.label}
              </a>
              {section.children && (
                <div className="ml-3 mb-1">
                  {section.children.map((child) => (
                    <a
                      key={child.id}
                      href={`#${child.id}`}
                      onClick={() => setActiveSection(child.id)}
                      className={`block px-2 py-1 rounded-md text-[13px] transition-colors ${
                        activeSection === child.id
                          ? "text-blue-400 font-medium"
                          : "text-slate-600 hover:text-slate-300"
                      }`}
                    >
                      {child.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Main content */}
        <div className="lg:ml-60 flex-1 min-w-0">
          <div className="max-w-3xl mx-auto px-8 py-12 space-y-20">

            {/* ── Overview ── */}
            <section id="overview">
              <Eyebrow>Overview</Eyebrow>
              <H1>Roadmap Radar</H1>
              <Lead>
                An AI-powered signal intelligence layer that ingests customer feedback from Canny,
                deduplicates and scores grouped requests, and generates sprint-ready product artifacts —
                PRDs, story-pointed tasks, and Cursor implementation plans.
              </Lead>
              <p className="text-slate-500 leading-relaxed mt-4">
                The system has two independent layers: an <strong className="text-slate-300">analysis pipeline</strong> that runs on-demand
                to process Canny boards, and a <strong className="text-slate-300">web application</strong> that serves the results,
                manages pipeline jobs, and triggers the AI agent chain on any grouped request.
              </p>
              <div className="mt-6 grid sm:grid-cols-3 gap-4">
                <InfoCard icon="🔍" title="Signal Intelligence">
                  Collapses 1,194 raw posts into 548 grouped requests. Detects 134 already-shipped features automatically.
                </InfoCard>
                <InfoCard icon="🤖" title="Agent Pipeline">
                  4-agent chain generates PRD → tasks → spec → Cursor build plan in under 2 minutes from any group.
                </InfoCard>
                <InfoCard icon="🔒" title="Developer Control">
                  AI writes the plan, not the code. Plans repo is isolated with a fine-grained PAT — zero product codebase access.
                </InfoCard>
              </div>
            </section>

            <Divider />

            {/* ── How It Works ── */}
            <section id="how-it-works">
              <Eyebrow>How It Works</Eyebrow>
              <H2>The Analysis Pipeline</H2>
              <p className="text-slate-500 leading-relaxed mb-8">
                Every analysis run executes nine sequential stages. Runs are enqueued in Redis — one job
                processes at a time. Each stage checks a cancellation flag, so jobs can be stopped cleanly
                at any stage boundary.
              </p>

              <section id="pipeline-stages">
                <H3>Pipeline Stages</H3>
                <div className="space-y-2.5 mt-4">
                  {[
                    { n: "01", name: "Ingest", desc: "Fetch posts, comments, and changelogs from the Canny API. Normalize vote scores to a 0–10 range. Output is stored per-board in an isolated subdirectory." },
                    { n: "02", name: "Summarize", desc: "GPT-4o generates a structured summary per post that incorporates the title, description, and all community comments. Captures the true request intent even when the original description is sparse." },
                    { n: "03", name: "Embed", desc: "OpenAI text-embedding-3-large produces a 3072-dimension vector for each summary. Embeddings are persisted to disk to avoid re-computation on re-runs." },
                    { n: "04", name: "Deduplicate", desc: "Hybrid search (FAISS cosine similarity + BM25 term matching) retrieves candidate pairs. Reciprocal Rank Fusion merges both result sets. GPT-4o makes the final deduplication decision on each candidate pair." },
                    { n: "05", name: "Extract Insights", desc: "GPT-4o generates per-post structured intelligence: customer pain points, customer needs, an impact score (0–10), impact reasoning, and a business impact assessment." },
                    { n: "06", name: "Summarize Changelogs", desc: "GPT-4o summarizes each published changelog entry filtered to the relevant product label. Changelog summaries are used as the reference corpus for completion detection." },
                    { n: "07", name: "Detect Completed Features", desc: "Hybrid search matches changelog summaries against all open requests. GPT-4o confirms which requests have already shipped, flagging them as completed." },
                    { n: "08", name: "Classify UI/UX Wins", desc: "GPT-4o evaluates each request to identify quick UI/UX improvements — small changes with high user impact that can be shipped in days, not sprints." },
                    { n: "09", name: "Merge", desc: "Combines groups, insights, completion flags, and UI/UX classifications into the final scored output (groups_with_insights.json), ranked by aggregate impact score." },
                  ].map((stage) => (
                    <div key={stage.n} className="flex gap-4 p-4 bg-[#161b22] border border-white/[0.07] rounded-xl hover:border-blue-500/30 transition-colors">
                      <div className="shrink-0 w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-blue-400 tracking-wider">{stage.n}</span>
                      </div>
                      <div>
                        <div className="font-semibold text-slate-200 text-sm mb-1">{stage.name}</div>
                        <p className="text-sm text-slate-500 leading-relaxed">{stage.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section id="deduplication" className="mt-12">
                <H3>Deduplication Engine</H3>
                <p className="text-slate-500 leading-relaxed mt-2 mb-6">
                  The hybrid approach is the core technical differentiator. Neither semantic search nor
                  keyword matching is sufficient on its own — the combination with LLM verification is
                  meaningfully more accurate than either method alone.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    { step: "Step 1 — Retrieve", title: "FAISS + BM25", desc: "Vector cosine similarity finds semantically similar posts. BM25 term matching finds lexically similar posts. Both candidate lists are combined with Reciprocal Rank Fusion." },
                    { step: "Step 2 — Verify", title: "GPT-4o", desc: "Each candidate pair is passed to GPT-4o for final confirmation. A pair is only marked as duplicate if the model agrees the two posts describe the same underlying need." },
                    { step: "Step 3 — Merge", title: "Group + Score", desc: "Confirmed duplicates are merged into a single group. Vote counts, pain points, and needs are aggregated across all posts in the group. Impact score is recalculated on the merged set." },
                  ].map((card) => (
                    <div key={card.step} className="bg-[#161b22] border border-white/[0.07] rounded-xl p-4">
                      <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">{card.step}</div>
                      <div className="font-semibold text-sm text-slate-200 mb-1">{card.title}</div>
                      <p className="text-xs text-slate-500 leading-relaxed">{card.desc}</p>
                    </div>
                  ))}
                </div>
              </section>
            </section>

            <Divider />

            {/* ── Agent Pipeline ── */}
            <section id="agent-pipeline">
              <Eyebrow>Agent Pipeline</Eyebrow>
              <H2>From Group to Sprint-Ready Task</H2>
              <p className="text-slate-500 leading-relaxed mb-8">
                Triggered on-demand from any group detail page. Runs a four-agent chain that produces
                a full PRD, story-pointed sub-tasks, a feature spec, and a Cursor-executable implementation
                plan — all in under two minutes. Results are persisted and auto-loaded on every subsequent visit.
              </p>

              <section id="agent-chain">
                <H3>4-Agent Chain</H3>
                <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.07]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#161b22] border-b border-white/[0.07]">
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-16">Agent</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-32">Model</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Role</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-24">Timing</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04]">
                      {[
                        { n: "1", model: "gpt-4o", role: "Generates a full PRD from the group's aggregated pain points, customer needs, and impact data. Includes problem statement, proposed solution, success metrics, and open questions.", timing: "~15s" },
                        { n: "2", model: "gpt-4o-mini", role: "Breaks the PRD into dev / QA / design sub-tasks with Fibonacci story point estimates (1–13). Uses the smaller model intentionally — this is structured extraction, not reasoning.", timing: "< 3s" },
                        { n: "3", model: "gpt-4o", role: "Generates a feature spec file per dev sub-task. Agents 3 and 4 run in parallel via asyncio — wall-clock time is ~15s regardless of task count.", timing: "~15s ‖" },
                        { n: "4", model: "gpt-5.4", role: "Generates a Cursor-executable implementation plan per dev task. Includes TDD steps, exact file paths, test commands, and Cursor skill invocations. Pushed to the isolated plans repo on GitHub.", timing: "~15s ‖" },
                      ].map((a) => (
                        <tr key={a.n} className="bg-[#0d1117] hover:bg-[#161b22] transition-colors">
                          <td className="px-4 py-3 font-bold text-slate-600 text-xs">#{a.n}</td>
                          <td className="px-4 py-3"><Code>{a.model}</Code></td>
                          <td className="px-4 py-3 text-slate-400 leading-relaxed">{a.role}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs font-mono">{a.timing}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-600 mt-2 ml-1">‖ = runs in parallel with the other agent</p>
              </section>

              <section id="agent-outputs" className="mt-10">
                <H3>Outputs</H3>
                <div className="grid sm:grid-cols-3 gap-4 mt-4">
                  <div className="bg-[#161b22] border border-white/[0.07] rounded-xl p-4">
                    <div className="text-violet-400 font-bold text-sm mb-2">ClickUp Epic</div>
                    <ul className="text-xs text-slate-500 space-y-1.5">
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Full PRD as Epic description</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Dev / QA / Design sub-tasks with <Code>[labels]</Code></li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Fibonacci story points embedded per sub-task</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> QA card auto-moved to "ready for qa"</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Scoped to per-board list in "Roadmap Radar" space</li>
                    </ul>
                  </div>
                  <div className="bg-[#161b22] border border-white/[0.07] rounded-xl p-4">
                    <div className="text-slate-300 font-bold text-sm mb-2">GitHub Build Plans</div>
                    <ul className="text-xs text-slate-500 space-y-1.5">
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> One <Code>.md</Code> plan per dev sub-task</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> TDD steps with exact file paths and test commands</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Cursor skill invocations included</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Pushed in parallel via asyncio</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Plans repo has zero access to the product codebase</li>
                    </ul>
                  </div>
                  <div className="bg-[#161b22] border border-white/[0.07] rounded-xl p-4">
                    <div className="text-emerald-400 font-bold text-sm mb-2">Developer Workflow</div>
                    <ul className="text-xs text-slate-500 space-y-1.5">
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> ClickUp sub-task gets a GitHub comment with plan link</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Developer opens plan in Cursor on their local checkout</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Developer reviews, adapts, and executes</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> Developer owns the commit</li>
                      <li className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">✓</span> AI never touches the product codebase</li>
                    </ul>
                  </div>
                </div>
              </section>
            </section>

            <Divider />

            {/* ── Architecture ── */}
            <section id="architecture">
              <Eyebrow>Architecture</Eyebrow>
              <H2>System Design</H2>

              <section id="system-overview">
                <H3>System Overview</H3>
                <p className="text-slate-500 leading-relaxed mt-2 mb-6">
                  The system is composed of a Next.js frontend, a FastAPI backend, Redis for job state and
                  queue management, and filesystem-backed data storage. External integrations (ClickUp, GitHub)
                  are called only from the agent pipeline, never from the analysis pipeline.
                </p>
                <div className="bg-[#010409] border border-white/[0.07] rounded-xl p-5 font-mono text-xs leading-relaxed overflow-x-auto">
                  <pre className="text-slate-400 whitespace-pre">{`┌─────────────────────────────────────────────────────────┐
│  Next.js Frontend (localhost:3000 / Vercel)              │
│  Dashboard · Groups · Group Detail · Jobs · Login        │
└───────────────────────┬─────────────────────────────────┘
                        │ HTTP
┌───────────────────────▼─────────────────────────────────┐
│  FastAPI Backend (localhost:8000 / Cloud Run)            │
│                                                          │
│  /api/jobs       Job CRUD, cancel, cleanup               │
│  /api/groups     Search, filter, paginate                │
│  /api/boards     Live Canny board fetch                  │
│  /api/agents     PRD stream, result, config              │
│  /api/auth       Google OAuth / demo mode                │
└──────────┬───────────────────────┬───────────────────────┘
           │                       │
┌──────────▼──────┐   ┌────────────▼────────────────────┐
│  Redis          │   │  Filesystem                      │
│  job hashes     │   │  data/<board>/groups_with_...    │
│  job queue      │   │  data/prd_results/<group_id>/    │
│  pipeline lock  │   └─────────────────────────────────┘
└─────────────────┘
                        │ Agent pipeline only
         ┌──────────────┼──────────────┐
         ▼              ▼              ▼
    ClickUp API    GitHub Plans    OpenAI API
    Epic + tasks   Cursor plans    GPT-4o / mini`}</pre>
                </div>
              </section>

              <section id="job-state-machine" className="mt-10">
                <H3>Job State Machine</H3>
                <p className="text-slate-500 leading-relaxed mt-2 mb-6">
                  Every pipeline run is a job in Redis. The state machine ensures exactly one job runs at a time,
                  cancellation is cooperative, and stale locks are cleared on server restart.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { state: "pending", color: "bg-amber-500/10 border-amber-500/20 text-amber-400", desc: "Created, waiting for the queue lock" },
                    { state: "queued", color: "bg-blue-500/10 border-blue-500/20 text-blue-400", desc: "Lock held by another job; will process next" },
                    { state: "processing", color: "bg-violet-500/10 border-violet-500/20 text-violet-400", desc: "Lock acquired; pipeline stages executing" },
                    { state: "completed", color: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400", desc: "All 9 stages succeeded; lock released" },
                    { state: "cancelling", color: "bg-orange-500/10 border-orange-500/20 text-orange-400", desc: "Cancel requested; pipeline checks flag at stage boundary" },
                    { state: "cancelled", color: "bg-white/5 border-white/10 text-slate-500", desc: "Stopped cleanly; lock released" },
                    { state: "failed", color: "bg-red-500/10 border-red-500/20 text-red-400", desc: "Unhandled error; lock released; job marked failed" },
                    { state: "stale", color: "bg-white/[0.03] border-white/[0.06] text-slate-600", desc: "Detected on startup; auto-marked failed; lock cleared" },
                  ].map((s) => (
                    <div key={s.state} className={`border rounded-xl p-3 ${s.color}`}>
                      <div className="font-bold text-xs mb-1 font-mono">{s.state}</div>
                      <p className="text-xs opacity-80 leading-snug">{s.desc}</p>
                    </div>
                  ))}
                </div>
                <Callout type="info" className="mt-5">
                  <strong>Automatic recovery:</strong> On startup, the backend scans Redis for any job stuck in <Code>processing</Code>
                  state, marks it <Code>failed</Code>, and releases the queue lock. A manual endpoint{" "}
                  <Code>POST /api/jobs/cleanup</Code> does the same without a restart.
                </Callout>
              </section>
            </section>

            <Divider />

            {/* ── Tech Stack ── */}
            <section id="tech-stack">
              <Eyebrow>Tech Stack</Eyebrow>
              <H2>Technologies</H2>
              <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.07]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#161b22] border-b border-white/[0.07]">
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-44">Layer</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Technologies</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {[
                      { layer: "Analysis Pipeline", tech: "Python 3.11, OpenAI GPT-4o, text-embedding-3-large, FAISS, rank_bm25" },
                      { layer: "Agent Pipeline", tech: "GPT-4o (PRD + spec), gpt-4o-mini (task breakdown), gpt-5.4 (Cursor plans), httpx async, asyncio for parallelism" },
                      { layer: "Backend API", tech: "FastAPI, Redis (async via aioredis), Pydantic v2, Google OAuth2 (google-auth)" },
                      { layer: "Frontend", tech: "Next.js 16, TypeScript, Tailwind CSS v4, NextAuth.js, Inter typeface" },
                      { layer: "External Integrations", tech: "ClickUp REST API v2, GitHub REST API v3 (plans repo, fine-grained PAT)" },
                      { layer: "Infrastructure (local)", tech: "Docker Compose — backend, frontend, Redis all containerized" },
                      { layer: "Infrastructure (prod)", tech: "GCP Cloud Run (backend), Cloud Memorystore (Redis), GCS (artifacts), Artifact Registry, Vercel (frontend)" },
                    ].map((row) => (
                      <tr key={row.layer} className="bg-[#0d1117] hover:bg-[#161b22] transition-colors">
                        <td className="px-4 py-3 font-semibold text-slate-300 text-sm align-top">{row.layer}</td>
                        <td className="px-4 py-3 text-slate-500 text-sm">{row.tech}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <Divider />

            {/* ── Configuration ── */}
            <section id="configuration">
              <Eyebrow>Configuration</Eyebrow>
              <H2>Environment Variables</H2>
              <p className="text-slate-500 leading-relaxed mb-8">
                All configuration lives in <Code>backend/.env</Code>. The system starts in demo and mock mode
                with no required env vars — explore the UI and pre-computed data without any API keys.
              </p>

              <section id="env-vars">
                <H3>Core Variables</H3>
                <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.07]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#161b22] border-b border-white/[0.07]">
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Variable</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-24">Default</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04]">
                      {[
                        { var: "DEMO_MODE", default: "true", desc: "Accepts any request as demo@gohighlevel.com. Set to false and configure Google OAuth for production." },
                        { var: "MOCK_PIPELINE", default: "true", desc: "Simulates all 9 pipeline stages with realistic delays and no API calls. Safe for demos." },
                        { var: "OPENAI_API_KEY", default: "—", desc: "Required for live pipeline runs and agent pipeline. Not needed in mock mode." },
                        { var: "CANNY_API_KEY", default: "—", desc: "Required for live pipeline runs to fetch posts, comments, and changelogs." },
                        { var: "CANNY_BOARD_ID", default: "—", desc: "Optional fallback board ID used when no board is specified in the job trigger." },
                        { var: "REDIS_URL", default: "redis://redis:6379", desc: "Redis connection URL. Uses the Docker Compose service name by default." },
                        { var: "GOOGLE_CLIENT_ID", default: "—", desc: "Required when DEMO_MODE=false. Google OAuth client ID restricted to @gohighlevel.com." },
                        { var: "GOOGLE_CLIENT_SECRET", default: "—", desc: "Required when DEMO_MODE=false." },
                      ].map((row) => (
                        <tr key={row.var} className="bg-[#0d1117] hover:bg-[#161b22] transition-colors">
                          <td className="px-4 py-3 align-top"><Code>{row.var}</Code></td>
                          <td className="px-4 py-3 text-slate-600 text-xs align-top font-mono">{row.default}</td>
                          <td className="px-4 py-3 text-slate-500 text-sm leading-relaxed">{row.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="agent-config" className="mt-10">
                <H3>Agent Model Overrides</H3>
                <p className="text-slate-500 text-sm leading-relaxed mt-2 mb-4">
                  Each agent in the chain has an independently configurable model. Defaults are shown below.
                </p>
                <CodeBlock>{`# ClickUp — required for Live mode
CLICKUP_API_TOKEN=pk_...

# GitHub — optional, independent of ClickUp
# Fine-grained PAT: Contents Read+Write on plans repo only
GITHUB_TOKEN=github_pat_...
GITHUB_PLANS_REPO=your-org/roadmap-radar-plans

# Model overrides (optional — defaults shown)
AGENT_PRD_MODEL=gpt-4o
AGENT_TASKS_MODEL=gpt-4o-mini
AGENT_SPEC_MODEL=gpt-4o
AGENT_CODE_MODEL=gpt-5.4`}</CodeBlock>
              </section>
            </section>

            <Divider />

            {/* ── Quick Start ── */}
            <section id="quick-start">
              <Eyebrow>Quick Start</Eyebrow>
              <H2>Running Locally</H2>
              <p className="text-slate-500 leading-relaxed mb-6">
                The entire stack — backend, frontend, and Redis — runs via Docker Compose. No env vars are
                required to start. Pre-computed analysis results from the Automations, Voice AI, and Call
                Tracking boards are included.
              </p>

              <H3>Start everything</H3>
              <CodeBlock>{`docker compose up --build`}</CodeBlock>

              <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.07]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#161b22] border-b border-white/[0.07]">
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Service</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">URL</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04] bg-[#0d1117]">
                    <tr className="hover:bg-[#161b22] transition-colors"><td className="px-4 py-3 font-medium text-slate-300">Frontend</td><td className="px-4 py-3"><Code>http://localhost:3000</Code></td></tr>
                    <tr className="hover:bg-[#161b22] transition-colors"><td className="px-4 py-3 font-medium text-slate-300">Backend API</td><td className="px-4 py-3"><Code>http://localhost:8000</Code></td></tr>
                    <tr className="hover:bg-[#161b22] transition-colors"><td className="px-4 py-3 font-medium text-slate-300">API Docs (Swagger)</td><td className="px-4 py-3"><Code>http://localhost:8000/docs</Code></td></tr>
                  </tbody>
                </table>
              </div>

              <H3 className="mt-8">Enable live pipeline</H3>
              <p className="text-slate-500 text-sm leading-relaxed mb-3">
                To run the real pipeline against your Canny board, add to <Code>backend/.env</Code>:
              </p>
              <CodeBlock>{`MOCK_PIPELINE=false
CANNY_API_KEY=your-canny-api-key
OPENAI_API_KEY=your-openai-api-key
CANNY_BOARD_ID=your-default-board-id   # optional fallback`}</CodeBlock>
              <p className="text-xs text-slate-600 mt-2">Then restart: <Code>docker compose restart backend</Code></p>

              <Callout type="tip" className="mt-6">
                Use <strong>Mock mode</strong> in the Jobs page to demo the full UI — all 9 stages simulate with realistic
                timing and no API calls. Select Mock when creating a new job.
              </Callout>
            </section>

            <Divider />

            {/* ── Deployment ── */}
            <section id="deployment">
              <Eyebrow>Deployment</Eyebrow>
              <H2>Deploying to Production</H2>
              <p className="text-slate-500 leading-relaxed mb-6">
                Production targets: <strong className="text-slate-300">GCP Cloud Run</strong> (backend),{" "}
                <strong className="text-slate-300">GCS</strong> (pipeline artifacts),{" "}
                <strong className="text-slate-300">Cloud Memorystore</strong> (Redis),{" "}
                <strong className="text-slate-300">Vercel</strong> (frontend).
                GCP infrastructure is already provisioned on staging.
              </p>

              <section id="scripts">
                <H3>Scripts Reference</H3>
                <p className="text-slate-500 text-sm leading-relaxed mt-2 mb-4">
                  All scripts live in <Code>scripts/</Code>. Each sources <Code>scripts/provision.env</Code>
                  for configuration. Run <Code>config.sh</Code> once to generate it.
                </p>
                <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.07]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#161b22] border-b border-white/[0.07]">
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Script</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Purpose</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-28">When</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04]">
                      {[
                        { script: "config.sh", purpose: "Interactive setup — writes scripts/provision.env with all config values", when: "Once" },
                        { script: "provision.sh", purpose: "Creates all GCP resources: Artifact Registry, GCS bucket, Memorystore instance, VPC connector", when: "Once/env" },
                        { script: "build.sh", purpose: "Builds and pushes the backend Docker image to Artifact Registry", when: "Every release" },
                        { script: "deploy.sh", purpose: "Deploys backend to Cloud Run and frontend to Vercel", when: "Every release" },
                        { script: "cleanup.sh", purpose: "Tears down all GCP resources. Supports --keep-data and --keep-images flags", when: "Decommission" },
                      ].map((row) => (
                        <tr key={row.script} className="bg-[#0d1117] hover:bg-[#161b22] transition-colors">
                          <td className="px-4 py-3"><Code>{row.script}</Code></td>
                          <td className="px-4 py-3 text-slate-500">{row.purpose}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{row.when}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <H3 className="mt-6">Deployment workflow</H3>
                <CodeBlock>{`# First time only
bash scripts/config.sh
source scripts/provision.env
bash scripts/provision.sh

# Every release
source scripts/provision.env
bash scripts/build.sh
bash scripts/deploy.sh

# Tear down
source scripts/provision.env
bash scripts/cleanup.sh [--keep-data] [--keep-images]`}</CodeBlock>
              </section>

              <section id="production-checklist" className="mt-10">
                <H3>Production Checklist</H3>
                <p className="text-slate-500 text-sm leading-relaxed mt-2 mb-5">
                  GCP infrastructure is provisioned. Remaining work is approximately 4–6 dev days plus one admin IAM action.
                </p>
                <div className="space-y-2">
                  {[
                    { done: true, item: "GCP infrastructure", detail: "Artifact Registry, GCS, Memorystore, VPC connector provisioned" },
                    { done: true, item: "Deployment scripts", detail: "config.sh → provision.sh → build.sh → deploy.sh workflow" },
                    { done: true, item: "Production server config", detail: "Dockerfile runs uvicorn without --reload" },
                    { done: false, item: "GCS artifact storage", detail: "storage.py reads local filesystem. Needs GCS R/W so output persists across Cloud Run instances.", effort: "1–2 days" },
                    { done: false, item: "HTTPS + custom domain", detail: "Cloud Run provides TLS on *.run.app. Custom domain needs DNS config.", effort: "0.5 days" },
                    { done: false, item: "Production auth", detail: "Set DEMO_MODE=false, configure Google OAuth client ID + secret.", effort: "1 day" },
                    { done: false, item: "Secrets management", detail: "Move API keys from plain env vars to GCP Secret Manager.", effort: "0.5 days" },
                    { done: false, item: "IAM permissions", detail: "github-actions-sa needs 5 project-level IAM roles. Blocked on org access.", effort: "0 dev days (admin action)" },
                    { done: false, item: "Health checks + observability", detail: "/health endpoint exists. Add readiness probe, Cloud Logging, Sentry.", effort: "1.5 days" },
                    { done: false, item: "Rate limiting", detail: "No per-IP or per-user rate limits on jobs or groups APIs.", effort: "0.5 days" },
                    { done: false, item: "CI/CD", detail: "GitHub Actions workflow exists. Blocked on same IAM action above.", effort: "0 dev days" },
                  ].map((row) => (
                    <div key={row.item} className="flex items-start gap-3 bg-[#161b22] border border-white/[0.07] rounded-xl px-4 py-3">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${row.done ? "bg-emerald-500/20" : "bg-white/5"}`}>
                        {row.done
                          ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-emerald-400"><polyline points="20 6 9 17 4 12" /></svg>
                          : <div className="w-2 h-2 rounded-full bg-slate-700" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm font-semibold ${row.done ? "text-slate-200" : "text-slate-400"}`}>{row.item}</span>
                          {!row.done && row.effort && (
                            <span className="text-[10px] font-medium text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">{row.effort}</span>
                          )}
                          {row.done && (
                            <span className="text-[10px] font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">Done</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{row.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </section>

            <Divider />

            {/* ── Integrations ── */}
            <section id="integrations">
              <Eyebrow>Integrations</Eyebrow>
              <H2>External Services</H2>

              <section id="clickup">
                <H3>ClickUp</H3>
                <p className="text-slate-500 text-sm leading-relaxed mt-2 mb-4">
                  The ClickUp integration creates a full Epic with sub-tasks when the agent pipeline runs in
                  Live mode. The integration auto-discovers the team ID from the API token, creates a
                  &quot;Roadmap Radar&quot; space if one doesn&apos;t exist, and scopes all work to a per-board list inside it.
                </p>
                <CodeBlock>{`# Required for agent pipeline Live mode
CLICKUP_API_TOKEN=pk_...`}</CodeBlock>
                <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.07]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#161b22] border-b border-white/[0.07]">
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Created</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04] bg-[#0d1117]">
                      <tr className="hover:bg-[#161b22] transition-colors"><td className="px-4 py-3 font-medium text-slate-300">Epic</td><td className="px-4 py-3 text-slate-500">Full PRD as description. Group title as Epic name.</td></tr>
                      <tr className="hover:bg-[#161b22] transition-colors"><td className="px-4 py-3 font-medium text-slate-300">Dev sub-tasks</td><td className="px-4 py-3 text-slate-500"><Code>[dev]</Code> label, Fibonacci story points, GitHub plan link in comment.</td></tr>
                      <tr className="hover:bg-[#161b22] transition-colors"><td className="px-4 py-3 font-medium text-slate-300">QA card</td><td className="px-4 py-3 text-slate-500">Auto-created and moved to &quot;ready for qa&quot; status.</td></tr>
                      <tr className="hover:bg-[#161b22] transition-colors"><td className="px-4 py-3 font-medium text-slate-300">Design tasks</td><td className="px-4 py-3 text-slate-500"><Code>[design]</Code> label, story points, created in the same Epic.</td></tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="github-plans" className="mt-10">
                <H3>GitHub Plans</H3>
                <p className="text-slate-500 text-sm leading-relaxed mt-2 mb-4">
                  Each dev sub-task gets a Cursor-executable plan pushed to an isolated GitHub repository.
                  The token is a fine-grained PAT scoped exclusively to the plans repo — zero access to
                  the product codebase. This is a deliberate architectural choice, not a limitation.
                </p>
                <CodeBlock>{`# Fine-grained PAT: Contents Read+Write on plans repo only
GITHUB_TOKEN=github_pat_...
GITHUB_PLANS_REPO=your-org/roadmap-radar-plans`}</CodeBlock>
                <Callout type="security" className="mt-4">
                  <strong>Security model:</strong> The fine-grained PAT has <strong>no access to your product codebase</strong>.
                  Even if the AI generates a flawed plan, nothing harmful can be committed anywhere.
                  Developers are always the gate between the plan and the code.
                </Callout>
                <H3 className="mt-6">Plan structure</H3>
                <p className="text-slate-500 text-sm leading-relaxed mb-3">Each plan file includes:</p>
                <CodeBlock>{`# Feature: [Sub-task name]

## Context
Problem statement, user issues, and proposed solution
derived from the PRD and group insights.

## Implementation Plan

### Step 1 — [File path]
TDD approach: write the test first.
\`\`\`
// test/path/to/spec.test.ts
describe('feature', () => { ... })
\`\`\`
Command: \`npm test path/to/spec\`

### Step 2 — [File path]
Implementation guidance with exact file paths.

## Cursor Skills
@skill brainstorming
@skill test-driven-development`}</CodeBlock>
              </section>
            </section>

            <Divider />

            {/* ── Future Enhancements ── */}
            <section id="future">
              <Eyebrow>Roadmap</Eyebrow>
              <H2>Future Enhancements</H2>
              <p className="text-slate-500 leading-relaxed mb-6">
                The architecture is designed to accommodate these additions without structural changes.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { title: "Pipeline worker isolation", desc: "Move the real pipeline out of the FastAPI thread pool into Celery, ARQ, or a dedicated worker process." },
                  { title: "Cross-board deduplication", desc: "Detect and merge duplicate requests that appear across multiple boards." },
                  { title: "Automatic Canny post merging", desc: "Once a group is validated, merge the duplicate posts directly in Canny so demand is consolidated at the source, not just in the internal tool." },
                  { title: "Automatic user replies and updates", desc: "Draft and send consistent updates back to customers on grouped requests — acknowledgements, status changes, and completion notifications." },
                  { title: "Scheduled pipeline runs", desc: "Automatically re-run the pipeline on a schedule (e.g., weekly) without manual triggering." },
                  { title: "Execution tracking", desc: "Track which Cursor plans have been completed by developers, linked back to ClickUp sub-task status." },
                  { title: "Sprint allocation", desc: "Auto-assign generated sub-tasks to an active sprint in ClickUp based on available capacity." },
                  { title: "Additional signal sources", desc: "Reddit discussions, YouTube comments, support tickets, Pendo analytics, GitHub issues — source adapter takes ~hours to add." },
                ].map((item) => (
                  <div key={item.title} className="bg-[#161b22] border border-white/[0.07] rounded-xl p-4 hover:border-blue-500/30 transition-colors">
                    <div className="font-semibold text-slate-200 text-sm mb-1">{item.title}</div>
                    <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Footer spacer */}
            <div className="pb-12 pt-4 border-t border-white/[0.06]">
              <p className="text-xs text-slate-700 text-center">
                Roadmap Radar · GoHighLevel Internal Tool · Dev Nitro 2026
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Typography helpers ────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div className="text-xs font-bold uppercase tracking-[0.15em] text-blue-500 mb-2">{children}</div>;
}
function H1({ children }: { children: React.ReactNode }) {
  return <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4 leading-tight">{children}</h1>;
}
function H2({ children }: { children: React.ReactNode }) {
  return <h2 className="text-2xl font-bold tracking-tight text-slate-100 mb-2 leading-snug">{children}</h2>;
}
function H3({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <h3 className={`text-base font-bold text-slate-200 ${className}`}>{children}</h3>;
}
function Lead({ children }: { children: React.ReactNode }) {
  return <p className="text-lg text-slate-400 leading-relaxed">{children}</p>;
}
function Divider() {
  return <hr className="border-white/[0.06]" />;
}
function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="font-mono text-[12px] bg-white/[0.07] text-blue-300 px-1.5 py-0.5 rounded-md border border-white/[0.09]">
      {children}
    </code>
  );
}
function CodeBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-[#010409] border border-white/[0.07] rounded-xl p-4 font-mono text-xs text-slate-400 leading-relaxed overflow-x-auto">
      <pre className="whitespace-pre">{children}</pre>
    </div>
  );
}

function InfoCard({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-[#161b22] border border-white/[0.07] rounded-xl p-4 hover:border-blue-500/30 transition-colors">
      <div className="text-xl mb-2">{icon}</div>
      <div className="font-semibold text-slate-200 text-sm mb-1">{title}</div>
      <p className="text-xs text-slate-500 leading-relaxed">{children}</p>
    </div>
  );
}

function Callout({ type, children, className = "" }: {
  type: "info" | "tip" | "security";
  children: React.ReactNode;
  className?: string;
}) {
  const styles = {
    info: "bg-blue-500/10 border-blue-500/20 text-blue-300",
    tip: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
    security: "bg-amber-500/10 border-amber-500/20 text-amber-300",
  }[type];
  const icons = { info: "ℹ", tip: "✓", security: "🔒" }[type];
  return (
    <div className={`border rounded-xl p-4 text-sm leading-relaxed ${styles} ${className}`}>
      <span className="font-bold mr-1.5">{icons}</span>
      {children}
    </div>
  );
}
